import {DateTime} from 'luxon';

/**
 * Recursively serializes Luxon DateTime objects to ISO strings while
 * avoiding infinite recursion from circular references.
 *
 * @param value The value to process.
 * @param seen A WeakSet to track visited objects.
 * @returns The value with DateTime instances replaced by ISO strings.
 */
export default function serializeDates<T>(
  value: T,
  seen: WeakSet<object> = new WeakSet(),
): T | string {
  // If it's a Luxon DateTime, convert it to an ISO string.
  if (DateTime.isDateTime(value)) {
    return value.toISO() as string;
  }

  // For objects (including arrays), check for circular references.
  if (value !== null && typeof value === 'object') {
    // If this object was seen already, return it as is to avoid loops.
    if (seen.has(value)) {
      return value;
    }

    seen.add(value);

    // Process arrays separately.
    if (Array.isArray(value)) {
      let didChange = false;

      const serializedArray = value.map((item) => {
        const serializedItem = serializeDates(item, seen);

        if (serializedItem !== item) {
          didChange = true;
        }

        return serializedItem;
      });

      return (didChange ? serializedArray : value) as T;
    }

    // For objects, recursively process each property.
    let didChange = false;

    const serializedObject: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(value)) {
      const serializedVal = serializeDates(val, seen);

      if (serializedVal !== val) {
        didChange = true;
      }

      serializedObject[key] = serializedVal;
    }

    return (didChange ? serializedObject : value) as T;
  }

  // For primitives, return the value as is.
  return value;
}
